/* eslint-disable indent */
/* eslint-disable react/jsx-indent */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AddIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useAppSelector } from '../../../../redux/hooks';
import { updateSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances';
import { getSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances/selectors';
import {
  getSelectedNetworkIndex,
  getSelectedNetworkRequest,
} from '../../../../redux/providerBuilder/selectedNetworkRequest/selectors';
import {
  checkIfVariableNameExists,
  createXPathFromElement,
  escapeRegExp,
  getMatchIndexes,
  isValidVariableName,
} from '../../../../utils/provider-builder/instance-selection-utils';

interface AddInstanceModalProps {
  searchInstanceInput: string;
  selectedQueryInstance: number;
  sections: any[];
}

const AddInstanceModal: React.FC<AddInstanceModalProps> = ({
  searchInstanceInput,
  selectedQueryInstance,
  sections,
}) => {
  const {
    isOpen: isVariableNameModelOpen,
    onOpen: onVariableNameModelOpen,
    onClose: onVariableNameModelClose,
  } = useDisclosure();

  const dispatch = useDispatch();

  const [variableNameOfIntanceInput, setVariableNameOfIntanceInput] =
    useState<string>('');
  const [error, setError] = useState<string>('');
  const [invariantChecked, setInvariantChecked] = useState<boolean>(false);

  const selectedInstances = useAppSelector(getSelectedInstances);
  const selectedNetworkRequest = useAppSelector(getSelectedNetworkRequest);
  const selectedNetworkIndex = useAppSelector(getSelectedNetworkIndex);

  function generateXpath(htmlString: string, index: number) {
    const lt = getMatchIndexes(htmlString.substring(0, index), '<');
    const gt = getMatchIndexes(htmlString.substring(index), '>');
    let element = null;
    const parser = new DOMParser();
    const parsed = parser.parseFromString(htmlString, 'text/html');
    const allElements = parsed.getElementsByTagName('*');
    for (const e of allElements) {
      if (e?.outerHTML) {
        if (
          e.outerHTML?.includes(
            htmlString.substring(lt[lt.length - 1], index + gt[0] + 1)
          )
        ) {
          if (element === null) {
            element = e;
          } else if (
            element?.outerHTML &&
            element?.outerHTML?.length > e.outerHTML.length
          ) {
            element = e;
          }
        }
      }
    }

    const xpath = createXPathFromElement(element, parsed) ?? '';
    return xpath;
  }

  function generateRegexHtml(
    htmlString: string,
    start: number,
    searchQueryInputsize: number,
    variableName?: string
  ) {
    const characterOffset = 20;
    let leftLimit = start > characterOffset ? start - characterOffset : 0;
    let sqoutes = false;
    let dqoutes = false;
    for (let i = 0; i < characterOffset && start - i > 0; i++) {
      const c = htmlString.charAt(start - i);
      if (c === '<') {
        leftLimit = start - i + 1;
        break;
      }

      if (c === "'") {
        if (sqoutes) {
          leftLimit = start - i + 1;
          break;
        }

        sqoutes = true;
      }

      if (c === '"') {
        if (dqoutes) {
          leftLimit = start - i + 1;
          break;
        }

        dqoutes = true;
      }
    }

    let sqoutesr = false;
    let dqoutesr = false;
    const claimDataEndPos = start + searchQueryInputsize;
    let rightLimit = claimDataEndPos + characterOffset;
    for (let i = 0; i < characterOffset && claimDataEndPos + i < htmlString.length; i++) {
      const c = htmlString.charAt(claimDataEndPos + i);
      if (c === '>') {
        rightLimit = claimDataEndPos + i + 1;
        break;
      }

      if (c === "'") {
        if (sqoutesr) {
          rightLimit = claimDataEndPos + i + 1;
          break;
        }

        sqoutesr = true;
      }

      if (c === '"') {
        if (dqoutesr) {
          rightLimit = claimDataEndPos + i + 1;
          break;
        }

        dqoutesr = true;
      }
    }

    const regex = escapeRegExp(
      htmlString
        .substring(leftLimit, rightLimit)
        .replace(searchInstanceInput, variableName ?? 'CLAIM_DATA')
    ).replace(variableName, '{{' + variableName + '}}');
    return regex;
  }

  function addSelectedInstanceToList(e: any) {
    if (!variableNameOfIntanceInput.trim()) {
      setError('Input cannot be empty');
      return;
    }

    if (checkIfVariableNameExists(selectedInstances, variableNameOfIntanceInput)) {
      setError('Variable name already exists');
      return;
    }

    if (!isValidVariableName(variableNameOfIntanceInput)) {
      setError('Special characters or spaces not allowed');
      return;
    }

    const xPath = generateXpath(
      selectedNetworkRequest[selectedNetworkIndex]?.response?.body,
      e.start
    );

    if (e.regx) {
      const regexMat = /({{.*?}})/g;
      const matches = e.regx.match(regexMat) || [];
      if (matches.length > 0) {
        e.regx = e.regx.replace(matches[0], '{{' + variableNameOfIntanceInput + '}}');
      }
    }

    const regex =
      e.regx ??
      generateRegexHtml(
        selectedNetworkRequest[selectedNetworkIndex]?.response?.body,
        e.start,
        searchInstanceInput.length,
        variableNameOfIntanceInput
      );
    const newSelectedInstanceList = {
      data: e.t,
      variableName: variableNameOfIntanceInput,
      responseSelection: {
        xPath: xPath,
        jsonPath: e.jsonPath ?? '',
        responseMatch: regex,
        invariant: invariantChecked,
        matchType: 'greedy',
        requestIndex: selectedNetworkIndex,
      },
    };

    dispatch(updateSelectedInstances(newSelectedInstanceList));

    setError('');
    setVariableNameOfIntanceInput('');
    onVariableNameModelClose();
  }

  return (
    <>
      <Tooltip
        label="Search or select the data you want to add"
        aria-label="A tooltip"
        isDisabled={!!searchInstanceInput.length}
      >
        <IconButton
          aria-label="add"
          backgroundColor="black"
          colorScheme="blackAlpha"
          isDisabled={!selectedQueryInstance}
          onClick={() => {
            setError('');
            setVariableNameOfIntanceInput('');
            onVariableNameModelOpen();
          }}
          icon={<AddIcon />}
        />
      </Tooltip>
      <Modal
        isOpen={isVariableNameModelOpen}
        onClose={onVariableNameModelClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Enter Variable Name</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl marginTop="1.5rem" isInvalid={!!error}>
              <Flex mt="15px" mb="15px">
                <FormLabel fontSize="md" mt="7px">
                  Variable Name:
                </FormLabel>
                <Flex direction="column">
                  <Input
                    borderRadius="5px"
                    borderWidth={1}
                    borderColor="black"
                    placeholder="eg. email, username, id"
                    required={true}
                    onChange={(e) => {
                      setVariableNameOfIntanceInput(e.target.value);
                    }}
                  />
                  <FormErrorMessage>{error}</FormErrorMessage>
                </Flex>
              </Flex>
              <Flex mt="15px" mb="15px">
                <FormLabel fontSize="md">Variable Value:</FormLabel>
                <FormLabel width="70%" fontSize="md" fontWeight="bold" ml="7px">
                  <Text
                    style={{
                      maxHeight: '350px',
                      overflow: 'auto',
                      padding: '1.5rem',
                      paddingBottom: '1.5rem',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                    backgroundColor="gray.200"
                  >
                    {searchInstanceInput}
                  </Text>
                </FormLabel>
              </Flex>
              {searchInstanceInput?.toString()?.length >= 300 && (
                <Flex mt="15px" mb="15px">
                  <Alert status="error">
                    <AlertIcon />
                    Character limit of 300 exceeded!
                    <br /> Proof may not be generated for this provider
                  </Alert>
                </Flex>
              )}

              <Flex mt="15px" mb="15px">
                <Flex direction="column">
                  <Tooltip label="To protect against duplication in future use">
                    <Checkbox
                      iconSize="1rem"
                      checked={invariantChecked}
                      onChange={() => {
                        setInvariantChecked(!invariantChecked);
                      }}
                    >
                      Unique to Each User
                    </Checkbox>
                  </Tooltip>
                </Flex>
              </Flex>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button onClick={onVariableNameModelClose} mr={3}>
              Cancel
            </Button>
            <Button
              bgColor="black"
              colorScheme="blackAlpha"
              onClick={() =>
                addSelectedInstanceToList(
                  sections.find((e: any) => e.i === selectedQueryInstance)
                )
              }
            >
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddInstanceModal;
